import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";

const tenantId = import.meta.env.VITE_AZURE_TENANT_ID;
const clientId = import.meta.env.VITE_AZURE_CLIENT_ID;
const apiScope = import.meta.env.VITE_AZURE_API_SCOPE;
const authorityOverride = import.meta.env.VITE_AZURE_AUTHORITY;
const authority = authorityOverride || `https://login.microsoftonline.com/${tenantId}`;
// Derived from the current origin, so every origin used must be registered in the SPA app registration's Authentication blade.
const redirectUri = `${window.location.origin}/auth/callback`;

export const MSAL_CONFIGURED = Boolean(tenantId && clientId && apiScope);

/** True inside MSAL's hidden silent-renew iframe (or any iframe). Cross-origin access throws. */
export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

if (!MSAL_CONFIGURED) {
  console.warn(
    "MSAL env vars missing (VITE_AZURE_TENANT_ID, VITE_AZURE_CLIENT_ID, VITE_AZURE_API_SCOPE). Microsoft sign-in will be disabled."
  );
}

// A custom authority must be whitelisted in knownAuthorities and served plain OIDC; AAD builds keep the defaults.
const authorityHost = authorityOverride
  ? new URL(authorityOverride).host
  : null;

export const msalInstance = MSAL_CONFIGURED
  ? new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri,
        postLogoutRedirectUri: `${window.location.origin}/login`,
        navigateToLoginRequestUrl: false,
        ...(authorityOverride
          ? {
              knownAuthorities: [authorityHost],
              protocolMode: "OIDC",
            }
          : {}),
      },
      cache: {
        // localStorage so a new tab inherits the signed-in account and can hydrate the session silently.
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
      },
    })
  : null;

// Await this before any other MSAL call; loginRedirect, acquireTokenSilent and handleRedirectPromise all require it.
let initPromise = null;
export const initMsal = () => {
  if (!msalInstance) return Promise.resolve();
  if (!initPromise) initPromise = msalInstance.initialize();
  return initPromise;
};

export const API_SCOPES = apiScope ? [apiScope] : [];

// The OIDC trio keeps the identity claims the backend and session hydration read on both the access and id tokens.
export const LOGIN_SCOPES = ["openid", "profile", "email", ...API_SCOPES];

export const getActiveAccount = () =>
  msalInstance ? msalInstance.getActiveAccount() : null;

export const getAllAccounts = () =>
  msalInstance ? msalInstance.getAllAccounts() : [];

// Promotes a cached account to active so a tab that never ran the redirect flow can still acquire tokens.
export const ensureActiveAccount = () => {
  if (!msalInstance) return null;
  const active = msalInstance.getActiveAccount();
  if (active) return active;
  const [first] = msalInstance.getAllAccounts();
  if (!first) return null;
  msalInstance.setActiveAccount(first);
  return first;
};

// Drops every cached Microsoft account and token, browser-wide.
export const clearMsalCache = async () => {
  if (!msalInstance) return;
  try {
    await initMsal();
    await msalInstance.clearCache();
    msalInstance.setActiveAccount(null);
  } catch (error) {
    console.warn("Could not clear the MSAL cache:", error);
  }
};

/** Returns a VAPAR API access token, null when no account is signed in, and throws InteractionRequiredAuthError when the user must re-authenticate. */
export const acquireApiAccessToken = async () => {
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  const result = await msalInstance.acquireTokenSilent({
    scopes: API_SCOPES,
    account,
  });
  return result.accessToken;
};

export { InteractionRequiredAuthError, BrowserAuthError };

const RETURN_URL_KEY = "msalPostLoginReturnUrl";

// Sends the user through interactive Microsoft auth, stashing the current URL so AuthCallbackPage can route them back.
let interactiveAuthInFlight = false;
export const requireInteractiveAuth = async () => {
  // Hidden-iframe reload would throw BrowserAuthError block_iframe_reload.
  if (!msalInstance || interactiveAuthInFlight || isInIframe()) return;
  interactiveAuthInFlight = true;
  await initMsal();
  try {
    sessionStorage.setItem(
      RETURN_URL_KEY,
      `${window.location.pathname}${window.location.search}`
    );
  } catch {
    // sessionStorage unavailable (private mode, quota); the user just lands on the default page instead.
  }
  const account = getActiveAccount();
  if (account) {
    await msalInstance.acquireTokenRedirect({ scopes: API_SCOPES, account });
  } else {
    await msalInstance.loginRedirect({ scopes: LOGIN_SCOPES });
  }
};

export const consumePostLoginReturnUrl = () => {
  try {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    sessionStorage.removeItem(RETURN_URL_KEY);
    return url || null;
  } catch {
    return null;
  }
};
